import { useEffect, useState } from 'react';

import validate from './ProviderInfoFormValidation';

import useLearnToSwimClubsData from '../../../../state/learnToSwimClubs/UseLearnToSwimClubsData';
import useLearnToSwimCurriculumData from '../../../../state/learnToSwimCurriculum/UseLearnToSwimCurriculumData';
import useLearnToSwimFileData from '../../../../state/learnToSwimFile/UseLearnToSwimFileData';

import UseForm from "@usas/ui-web/utils/UseForm";
import Constants from '@usas/ui-web/utils/Constants';
import { formatDate, getTodaysDate } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_FORM_STATE = {
  entityName: '',
  tradeName: '',
  taxId: '',
  learnToSwimCurriculumId: Constants.DEFAULT_ID,
  learnToSwimCurriculumName: '--',
  website: '',
  offersFinancialAssistance: '',
  currentLTSGrantRecipient: '',
  affiliatedWithUsasClub: '',
  childProtectionTraining: '',
  offerAdultLearnToSwim: '',
  offerSpecialNeeds: '',
  backgroundCheckAcknowledgement: '',
  backgroundCheckNotUsedExplanation: '',
  indemnificationAgreement: '',
  indemnificationAgreementSignature: '',
  indemnificationAgreementSignatureDate: Constants.BLANK_DATE_STRING,
  usasClubId: '',
  usasClubName: '',
  logoUrl: '',
  logoFile: {},
  curriculumUrl: '',
  curriculumFile: {},
  proofOfInsuranceUrl: '',
  proofOfInsuranceFile: {},
  formW9Url: '',
  formW9File: {}
};

const useProviderInfoForm = (onSubmitFormCallback, providerObj) => {
  const { ltsClubsState } = useLearnToSwimClubsData();
  const { ltsCurriculumState } = useLearnToSwimCurriculumData();
  const { ltsLogoFileUploadState, postLTSLogoFile, ltsCurriculumFileUploadState, postLTSCurriculumFile,
    ltsProofOfInsuranceFileUploadState, postLTSProofOfInsuranceFile, ltsFormW9FileUploadState, postLTSFormW9File, getLTSFile
  } = useLearnToSwimFileData();
  const [state, setState] = useState({ trySubmit: false, fixFiles: true });
  const { formState, errorState, isDirty, setIsDirty, setFormState, handleSubmit, updateFormState, onValueTextPairChanged,
    resetForm, setErrors, setFormData
  } = UseForm(INITIAL_FORM_STATE, localSubmitFormCallback, validate);

  const resetFormFunction = () => {
    resetForm();
    setState({ ...state, fixFiles: true });
  }

  const onLogoFileLoad = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setErrors({ ...errorState, logoFile: '' });
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const fileType = e.target.files[0].type;

      if (fileType === Constants.FILE_TYPE_JPG) {
        postLTSLogoFile(selectedFile);
        updateFormState('logoFile', selectedFile);
      } else {
        setErrors({ ...errorState, logoFile: 'File type not permitted. Please provide a (*.jpg) file.' });
      }
    }
  };

  const onLogoFileUnload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setFormState({
      ...formState,
      logoFile: {},
      logoUrl: ''
    });
  };

  const onLogoFileDownload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(formState.logoUrl);
  };

  const onCurriculumFileLoad = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setErrors({ ...errorState, curriculumFile: '' });
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const fileType = e.target.files[0].type;

      if (fileType === Constants.FILE_TYPE_PDF || fileType === Constants.FILE_TYPE_DOC ||
        fileType === Constants.FILE_TYPE_DOCX) {
        postLTSCurriculumFile(selectedFile);
        updateFormState('curriculumFile', selectedFile);
      } else {
        setErrors({ ...errorState, curriculumFile: 'File type not permitted. Please provide a (*.pdf), (*.doc), or (*.docx) file.' });
      }
    }
  };

  const onCurriculumFileUnload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setFormState({
      ...formState,
      curriculumFile: {},
      curriculumUrl: ''
    });
  };

  const onCurriculumFileDownload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(formState.curriculumUrl);
  };

  const onProofOfInsuranceFileLoad = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setErrors({ ...errorState, proofOfInsuranceFile: '' });
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const fileType = e.target.files[0].type;

      if (fileType === Constants.FILE_TYPE_PDF || fileType === Constants.FILE_TYPE_DOC ||
        fileType === Constants.FILE_TYPE_DOCX) {
        postLTSProofOfInsuranceFile(selectedFile);
        updateFormState('proofOfInsuranceFile', selectedFile);
      } else {
        setErrors({ ...errorState, proofOfInsuranceFile: 'File type not permitted. Please provide a (*.pdf), (*.doc), or (*.docx) file.' });
      }
    }
  };

  const onProofOfInsuranceFileUnload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setFormState({
      ...formState,
      proofOfInsuranceFile: {},
      proofOfInsuranceUrl: ''
    });
  };

  const onProofOfInsuranceFileDownload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(formState.proofOfInsuranceUrl);
  };

  const onFormW9FileLoad = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setErrors({ ...errorState, formW9File: '' });
    if (e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const fileType = e.target.files[0].type;

      if (fileType === Constants.FILE_TYPE_PDF || fileType === Constants.FILE_TYPE_DOC ||
        fileType === Constants.FILE_TYPE_DOCX) {
        postLTSFormW9File(selectedFile);
        updateFormState('formW9File', selectedFile);
      } else {
        setErrors({ ...errorState, formW9File: 'File type not permitted. Please provide a (*.pdf), (*.doc), or (*.docx) file.' });
      }
    }
  };

  const onFormW9FileUnload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setFormState({
      ...formState,
      formW9File: {},
      formW9Url: ''
    });
  };

  const onFormW9FileDownload = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    showUploadedFile(formState.formW9Url);
  };

  useEffect(() => {
    if (providerObj && ltsClubsState.areItemsLoaded === true && ltsCurriculumState.areItemsLoaded === true) {
      let usasClub = null;
      let learnToSwimCurriculum = null;

      if (providerObj.affiliatedWithUsasClub === true && providerObj.usasClubId) {
        usasClub = ltsClubsState.items.find(x => x.id === providerObj.usasClubId);
      }

      if (providerObj.learnToSwimCurriculumId > 0) {
        learnToSwimCurriculum = ltsCurriculumState.items.find(x => x.id === providerObj.learnToSwimCurriculumId);
      }

      setFormData({
        ...formState,
        entityName: providerObj.entityName || '',
        tradeName: providerObj.tradeName || '',
        taxId: providerObj.taxId || '',
        learnToSwimCurriculumId: learnToSwimCurriculum?.id || Constants.DEFAULT_ID,
        learnToSwimCurriculumName: learnToSwimCurriculum?.name || '--',
        website: providerObj.website || '',
        offersFinancialAssistance: providerObj.offersFinancialAssistance === true || providerObj.offersFinancialAssistance === false
          ? providerObj.offersFinancialAssistance
          : '',
        currentLTSGrantRecipient: providerObj.currentLTSGrantRecipient === true || providerObj.currentLTSGrantRecipient === false
          ? providerObj.currentLTSGrantRecipient
          : '',
        affiliatedWithUsasClub: providerObj.affiliatedWithUsasClub === true || providerObj.affiliatedWithUsasClub === false
          ? providerObj.affiliatedWithUsasClub
          : '',
        childProtectionTraining: providerObj.childProtectionTraining === true || providerObj.childProtectionTraining === false
          ? providerObj.childProtectionTraining
          : '',
        offerAdultLearnToSwim: providerObj.offerAdultLearnToSwim === true || providerObj.offerAdultLearnToSwim === false
          ? providerObj.offerAdultLearnToSwim
          : '',
        offerSpecialNeeds: providerObj.offerSpecialNeeds === true || providerObj.offerSpecialNeeds === false
          ? providerObj.offerSpecialNeeds
          : '',
        backgroundCheckAcknowledgement: providerObj.backgroundCheckAcknowledgement === true || providerObj.backgroundCheckAcknowledgement === false
          ? providerObj.backgroundCheckAcknowledgement
          : '',
        backgroundCheckNotUsedExplanation: providerObj.backgroundCheckAcknowledgement === false && providerObj.backgroundCheckNotUsedExplanation ?
          providerObj.backgroundCheckNotUsedExplanation : '',
        indemnificationAgreement: providerObj.indemnificationAgreement === true || providerObj.indemnificationAgreement === false
          ? providerObj.indemnificationAgreement
          : '',
        indemnificationAgreementSignature: providerObj.indemnificationAgreementSignature || '',
        indemnificationAgreementSignatureDate: providerObj.indemnificationAgreementSignatureDate && providerObj.indemnificationAgreementSignatureDate !== Constants.BLANK_DATE_STRING ? formatDate(providerObj.indemnificationAgreementSignatureDate) : getTodaysDate(),
        usasClubId: usasClub?.id || '',
        usasClubName: usasClub?.name || '',
        logoUrl: providerObj.logo || '',
        curriculumUrl: providerObj.curriculumUrl || '',
        proofOfInsuranceUrl: providerObj.proofOfInsuranceUrl || '',
        formW9Url: providerObj.formW9Url || '',
      });
      setState({ ...state, fixFiles: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerObj, ltsClubsState.areItemsLoaded, ltsCurriculumState.areItemsLoaded, state.fixFiles]);

  //TODO better way to do this?
  useEffect(() => {
    if (isDirty !== true) {
      setIsDirty(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    if (state.trySubmit === true) {
      if (ltsLogoFileUploadState.isObjLoading === false &&
        (ltsLogoFileUploadState.isObjLoaded === true && ltsLogoFileUploadState.objData.uploadUrl ||
          ltsCurriculumFileUploadState.isObjLoaded === true && ltsCurriculumFileUploadState.objData.uploadUrl ||
          ltsProofOfInsuranceFileUploadState.isObjLoaded === true && ltsProofOfInsuranceFileUploadState.objData.uploadUrl ||
          ltsFormW9FileUploadState.isObjLoaded === true && ltsFormW9FileUploadState.objData.uploadUrl)) {
        onSubmitFormCallback({
          ...formState,
          logoUrl: ltsLogoFileUploadState.objData.uploadUrl ? ltsLogoFileUploadState.objData.uploadUrl : formState.logoUrl || null,
          curriculumUrl: ltsCurriculumFileUploadState.objData.uploadUrl ? ltsCurriculumFileUploadState.objData.uploadUrl : formState.curriculumUrl || null,
          proofOfInsuranceUrl: ltsProofOfInsuranceFileUploadState.objData.uploadUrl ? ltsProofOfInsuranceFileUploadState.objData.uploadUrl : formState.proofOfInsuranceUrl || null,
          formW9Url: ltsFormW9FileUploadState.objData.uploadUrl ? ltsFormW9FileUploadState.objData.uploadUrl : formState.formW9Url || null
        });
        setState({ ...state, trySubmit: false });
      }
      else if (ltsLogoFileUploadState.isObjLoading === false && ltsCurriculumFileUploadState.isObjLoading === false &&
        ltsProofOfInsuranceFileUploadState.isObjLoading === false && ltsFormW9FileUploadState.isObjLoading === false) {
        onSubmitFormCallback({
          ...formState
        });
        setState({ ...state, trySubmit: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, ltsLogoFileUploadState, ltsCurriculumFileUploadState, ltsProofOfInsuranceFileUploadState, ltsFormW9FileUploadState])

  async function showUploadedFile(fileUrl) {
    if (fileUrl) {
      const getFileResult = await getLTSFile(fileUrl);

      if (getFileResult) {
        if (getFileResult.objData?.blobFile?.size > 0) {
          var file = window.URL.createObjectURL(getFileResult.objData?.blobFile);
          const link = document.createElement('a');

          link.href = file;
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        }
      }
    }
  }

  function localSubmitFormCallback(formState) {
    if ((!formState.curriculumFile?.size && !formState.curriculumUrl)
      || (!formState.proofOfInsuranceFile?.size && !formState.proofOfInsuranceUrl)
      || (!formState.formW9File?.size && !formState.formW9Url)) {
      setErrors({
        ...errorState,
        curriculumFile: formState.curriculumFile?.size ? '' : 'A file upload is required',
        proofOfInsuranceFile: formState.proofOfInsuranceFile?.size ? '' : 'A file upload is required',
        formW9File: formState.formW9File?.size ? '' : 'A file upload is required'
      });
    }
    else {
      setState({ ...state, trySubmit: true });
    }
  }

  return {
    isLoading: ltsClubsState.isArrayLoading || ltsCurriculumState.isArrayLoading,
    isSaving: ltsLogoFileUploadState.isObjLoading || ltsCurriculumFileUploadState.isObjLoading ||
      ltsProofOfInsuranceFileUploadState.isObjLoading || ltsFormW9FileUploadState.isObjLoading,
    formState,
    errorState,
    handleSubmit,
    updateFormState,
    onValueTextPairChanged,
    resetForm,
    onLogoFileLoad,
    onLogoFileUnload,
    onLogoFileDownload,
    onCurriculumFileLoad,
    onCurriculumFileUnload,
    onCurriculumFileDownload,
    onProofOfInsuranceFileLoad,
    onProofOfInsuranceFileUnload,
    onProofOfInsuranceFileDownload,
    onFormW9FileLoad,
    onFormW9FileUnload,
    onFormW9FileDownload,
    resetFormFunction
  };
};

export default useProviderInfoForm;